import type { TelegramWidgetUser, TokenResponse } from './contracts.ts'
import { ApiError, createApiClient, type ApiClient } from './http.ts'

export interface SessionOptions {
  baseUrl: string
  headers?: Readonly<Record<string, string>>
  /**
   * Свежие initData Telegram для повторного входа, когда refresh не помог
   * (в Mini App они есть при каждом запуске). `null` — вне Telegram.
   */
  getInitData?: () => string | null
  fetch?: typeof fetch
}

export interface Session {
  api: ApiClient
  loginWithTelegram: (initData: string) => Promise<void>
  /** Вход в браузере: данные Telegram Login Widget как есть. */
  loginWithWidget: (user: TelegramWidgetUser) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: () => boolean
  /** Вызывается, когда сессия потеряна и восстановить её не удалось. */
  onExpired: (listener: () => void) => () => void
}

/**
 * Токены живут только в памяти вкладки: не в localStorage и не в cookies.
 * Перезагрузка страницы в Mini App просто входит заново по initData.
 */
export function createSession(options: SessionOptions): Session {
  let accessToken: string | null = null
  let refreshToken: string | null = null
  let refreshing: Promise<boolean> | null = null
  const expiredListeners = new Set<() => void>()

  const api = createApiClient({
    baseUrl: options.baseUrl,
    headers: options.headers,
    fetch: options.fetch,
    getAccessToken: () => accessToken,
    refreshAccessToken: () => {
      refreshing ??= renew().finally(() => {
        refreshing = null
      })
      return refreshing
    },
  })

  function store(tokens: TokenResponse): void {
    accessToken = tokens.access_token
    refreshToken = tokens.refresh_token
  }

  function clear(): void {
    accessToken = null
    refreshToken = null
  }

  async function loginWithTelegram(initData: string): Promise<void> {
    store(await api.post<TokenResponse>('/auth/telegram', { init_data: initData }, { anonymous: true }))
  }

  async function loginWithWidget(user: TelegramWidgetUser): Promise<void> {
    store(await api.post<TokenResponse>('/auth/telegram-widget', user, { anonymous: true }))
  }

  async function renew(): Promise<boolean> {
    if (refreshToken !== null) {
      try {
        store(await api.post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken }, { anonymous: true }))
        return true
      } catch (error) {
        if (error instanceof ApiError && error.isUnavailable) return false
      }
    }
    const initData = options.getInitData?.() ?? null
    if (initData !== null) {
      try {
        await loginWithTelegram(initData)
        return true
      } catch {
        // упадём в expired ниже
      }
    }
    clear()
    for (const listener of expiredListeners) listener()
    return false
  }

  async function logout(): Promise<void> {
    const token = refreshToken
    clear()
    if (token === null) return
    try {
      await api.post('/auth/logout', { refresh_token: token }, { anonymous: true })
    } catch {
      // выход локальный в любом случае: refresh отзовётся по сроку
    }
  }

  return {
    api,
    loginWithTelegram,
    loginWithWidget,
    logout,
    isAuthenticated: () => accessToken !== null,
    onExpired: (listener) => {
      expiredListeners.add(listener)
      return () => expiredListeners.delete(listener)
    },
  }
}
